import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, Shield, User, AlertCircle, Plus, Power, Search, X } from 'lucide-react';
import { formatNumberES } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const Administracion = () => {
  const { API, token, usuario } = useContext(AuthContext);
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [usuariosFiltrados, setUsuariosFiltrados] = useState([]);
  const [filtroBusqueda, setFiltroBusqueda] = useState('');
  const [filtroRol, setFiltroRol] = useState('todos'); // 'todos', 'admin', 'usuario'
  const [filtroEstado, setFiltroEstado] = useState('todos'); // 'todos', 'activo', 'inactivo'
  const [loading, setLoading] = useState(true);
  const [editingUsuario, setEditingUsuario] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deleteUsuarioId, setDeleteUsuarioId] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    especialidad: '',
    rol: 'usuario',
    password: ''
  });

  // Verificar que el usuario sea admin
  useEffect(() => {
    if (usuario && usuario.rol !== 'admin') {
      toast.error('No tienes permisos para acceder a esta página');
      navigate('/');
    }
  }, [usuario, navigate]);

  useEffect(() => {
    if (usuario?.rol === 'admin') {
      cargarUsuarios();
    }
    // eslint-disable-next-line
  }, [usuario]);

  const cargarUsuarios = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/usuarios`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Asegurar que los valores booleanos se conviertan correctamente
      const usuariosConEstado = response.data.map(usr => {
        let activoValue = usr.activo;
        // Convertir diferentes formatos a booleano
        if (typeof activoValue === 'string') {
          activoValue = activoValue === 'true' || activoValue === '1';
        } else if (typeof activoValue === 'number') {
          activoValue = activoValue === 1;
        } else {
          activoValue = activoValue === true;
        }
        return {
          ...usr,
          activo: activoValue
        };
      });
      setUsuarios(usuariosConEstado);
      aplicarFiltros(usuariosConEstado);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('No tienes permisos para acceder a esta página');
        navigate('/');
      } else {
        toast.error('Error al cargar usuarios');
      }
    } finally {
      setLoading(false);
    }
  };

  const aplicarFiltros = (listaUsuarios = usuarios) => {
    let filtrados = [...listaUsuarios];

    // Filtro por búsqueda (nombre, email, especialidad)
    if (filtroBusqueda.trim() !== '') {
      const busqueda = filtroBusqueda.toLowerCase();
      filtrados = filtrados.filter(usr =>
        usr.nombre.toLowerCase().includes(busqueda) ||
        usr.email.toLowerCase().includes(busqueda) ||
        (usr.especialidad && usr.especialidad.toLowerCase().includes(busqueda))
      );
    }

    // Filtro por rol
    if (filtroRol !== 'todos') {
      filtrados = filtrados.filter(usr => usr.rol === filtroRol);
    }

    // Filtro por estado
    if (filtroEstado !== 'todos') {
      const estadoActivo = filtroEstado === 'activo';
      filtrados = filtrados.filter(usr => usr.activo === estadoActivo);
    }

    setUsuariosFiltrados(filtrados);
  };

  // Aplicar filtros cuando cambien los filtros o los usuarios
  useEffect(() => {
    if (usuarios.length > 0) {
      aplicarFiltros(usuarios);
    }
    // eslint-disable-next-line
  }, [filtroBusqueda, filtroRol, filtroEstado, usuarios]);

  const abrirDialogEditar = (usuario) => {
    setEditingUsuario(usuario);
    setFormData({
      nombre: usuario.nombre,
      email: usuario.email,
      especialidad: usuario.especialidad || '',
      rol: usuario.rol || 'usuario',
      password: ''
    });
    setIsEditDialogOpen(true);
  };

  const abrirDialogCrear = () => {
    setEditingUsuario(null);
    setFormData({
      nombre: '',
      email: '',
      especialidad: '',
      rol: 'usuario',
      password: ''
    });
    setIsCreateDialogOpen(true);
  };

  const cerrarDialogEditar = () => {
    setIsEditDialogOpen(false);
    setEditingUsuario(null);
    setFormData({
      nombre: '',
      email: '',
      especialidad: '',
      rol: 'usuario',
      password: ''
    });
  };

  const cerrarDialogCrear = () => {
    setIsCreateDialogOpen(false);
    setFormData({
      nombre: '',
      email: '',
      especialidad: '',
      rol: 'usuario',
      password: ''
    });
  };

  const guardarUsuario = async () => {
    try {
      await axios.put(
        `${API}/admin/usuarios/${editingUsuario.id}`,
        {
          nombre: formData.nombre,
          email: formData.email,
          especialidad: formData.especialidad,
          rol: formData.rol
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Usuario actualizado exitosamente');
      cerrarDialogEditar();
      cargarUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    }
  };

  const crearUsuario = async () => {
    if (!formData.password || formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/usuarios`,
        {
          nombre: formData.nombre,
          email: formData.email,
          especialidad: formData.especialidad,
          rol: formData.rol,
          password: formData.password
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success('Usuario creado exitosamente');
      cerrarDialogCrear();
      cargarUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const toggleActivo = async (usuario) => {
    // Obtener el estado actual del usuario desde la lista actualizada
    const usuarioActual = usuarios.find(u => u.id === usuario.id);
    if (!usuarioActual) {
      toast.error('Usuario no encontrado');
      return;
    }
    
    // Normalizar el estado actual a booleano
    const estadoActual = Boolean(usuarioActual.activo === true || usuarioActual.activo === 1 || usuarioActual.activo === '1' || usuarioActual.activo === 'true');
    const nuevoEstado = !estadoActual;
    
    try {
      const response = await axios.put(
        `${API}/admin/usuarios/${usuario.id}`,
        {
          activo: nuevoEstado
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Normalizar el estado recibido del servidor
      const estadoRecibido = response.data.activo;
      const estadoNormalizado = Boolean(
        estadoRecibido === true || 
        estadoRecibido === 1 || 
        estadoRecibido === '1' || 
        estadoRecibido === 'true'
      );
      
      toast.success(`Usuario ${estadoNormalizado ? 'activado' : 'desactivado'} exitosamente`);
      
      // Actualizar inmediatamente el estado local
      setUsuarios(prevUsuarios => {
        const actualizados = prevUsuarios.map(u => 
          u.id === usuario.id 
            ? { ...u, activo: estadoNormalizado }
            : u
        );
        // Aplicar filtros inmediatamente con los nuevos datos
        aplicarFiltros(actualizados);
        return actualizados;
      });
      
      // Recargar usuarios después de un breve delay para sincronización completa
      setTimeout(() => {
        cargarUsuarios();
      }, 300);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado del usuario');
    }
  };

  const abrirDialogEliminar = (usuarioId) => {
    setDeleteUsuarioId(usuarioId);
    setIsDeleteDialogOpen(true);
  };

  const eliminarUsuario = async () => {
    try {
      await axios.delete(`${API}/admin/usuarios/${deleteUsuarioId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuario eliminado exitosamente');
      setIsDeleteDialogOpen(false);
      setDeleteUsuarioId(null);
      cargarUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  if (usuario?.rol !== 'admin') {
    return null;
  }

  return (
    <Layout>
      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title flex items-center gap-3">
            <Shield size={28} className="text-purple-600" />
            Administración de Usuarios
          </h1>
          <p className="page-subtitle">Gestiona los usuarios del sistema</p>
        </div>

        <div className="page-body">
          <div className="mb-4 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            {/* Filtros */}
            <div className="flex flex-wrap gap-3 flex-1">
              <div className="flex-1 min-w-[200px]">
                <Input
                  placeholder="Buscar por nombre, email o especialidad..."
                  value={filtroBusqueda}
                  onChange={(e) => setFiltroBusqueda(e.target.value)}
                  className="w-full"
                />
              </div>
              <Select value={filtroRol} onValueChange={setFiltroRol}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Rol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los roles</SelectItem>
                  <SelectItem value="admin">Administradores</SelectItem>
                  <SelectItem value="usuario">Usuarios</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="activo">Activos</SelectItem>
                  <SelectItem value="inactivo">Inactivos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={abrirDialogCrear} className="bg-purple-600 hover:bg-purple-700">
              <Plus size={18} className="mr-2" />
              Crear Usuario
            </Button>
          </div>
          
          <Card className="card">
            {loading ? (
              <div className="text-center py-12">
                <div className="spinner mx-auto"></div>
                <p className="text-gray-500 mt-4">Cargando usuarios...</p>
              </div>
            ) : usuarios.length === 0 ? (
              <div className="text-center py-12">
                <User size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">No hay usuarios registrados</p>
              </div>
            ) : usuariosFiltrados.length === 0 ? (
              <div className="text-center py-12">
                <User size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">No se encontraron usuarios con los filtros aplicados</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => {
                    setFiltroBusqueda('');
                    setFiltroRol('todos');
                    setFiltroEstado('todos');
                  }}
                >
                  Limpiar filtros
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="mb-4 text-sm text-gray-600">
                  Mostrando {usuariosFiltrados.length} de {usuarios.length} usuarios
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Especialidad</TableHead>
                      <TableHead>Rol</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Fecha Registro</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {usuariosFiltrados.map((usr) => (
                      <TableRow key={usr.id}>
                        <TableCell className="font-medium">{usr.nombre}</TableCell>
                        <TableCell>{usr.email}</TableCell>
                        <TableCell>{usr.especialidad || '-'}</TableCell>
                        <TableCell>
                          <Badge variant={usr.rol === 'admin' ? 'default' : 'secondary'}>
                            {usr.rol === 'admin' ? (
                              <>
                                <Shield size={14} className="mr-1" />
                                Admin
                              </>
                            ) : (
                              <>
                                <User size={14} className="mr-1" />
                                Usuario
                              </>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleActivo(usr)}
                              disabled={usr.id === usuario?.id}
                              className={`flex items-center gap-2 transition-all ${
                                usr.activo 
                                  ? 'text-green-600 hover:text-green-700 hover:bg-green-50' 
                                  : 'text-gray-500 hover:text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              <Power 
                                size={18} 
                                className={usr.activo ? 'text-green-600' : 'text-gray-500'} 
                                style={usr.activo ? {} : { opacity: 0.5 }}
                              />
                              <span className="font-medium">{usr.activo ? 'Activo' : 'Inactivo'}</span>
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          {usr.created_at
                            ? new Date(usr.created_at).toLocaleDateString('es-ES')
                            : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => abrirDialogEditar(usr)}
                            >
                              <Edit size={16} />
                            </Button>
                            {usr.id !== usuario?.id && (
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => abrirDialogEliminar(usr.id)}
                              >
                                <Trash2 size={16} />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Dialog de edición */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre</Label>
              <Input
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="especialidad">Especialidad</Label>
              <Input
                id="especialidad"
                value={formData.especialidad}
                onChange={(e) => setFormData({ ...formData, especialidad: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rol">Rol</Label>
              <Select
                value={formData.rol}
                onValueChange={(value) => setFormData({ ...formData, rol: value })}
              >
                <SelectTrigger id="rol">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="usuario">
                    <div className="flex items-center gap-2">
                      <User size={16} />
                      Usuario
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <Shield size={16} />
                      Administrador
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cerrarDialogEditar}>
              Cancelar
            </Button>
            <Button onClick={guardarUsuario}>
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de creación */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear Nuevo Usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="nombre-crear">Nombre</Label>
              <Input
                id="nombre-crear"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email-crear">Email</Label>
              <Input
                id="email-crear"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="especialidad-crear">Especialidad</Label>
              <Input
                id="especialidad-crear"
                value={formData.especialidad}
                onChange={(e) => setFormData({ ...formData, especialidad: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rol-crear">Rol</Label>
              <Select
                value={formData.rol}
                onValueChange={(value) => setFormData({ ...formData, rol: value })}
              >
                <SelectTrigger id="rol-crear">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="usuario">
                    <div className="flex items-center gap-2">
                      <User size={16} />
                      Usuario
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <Shield size={16} />
                      Administrador
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password-crear">Contraseña</Label>
              <Input
                id="password-crear"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Mínimo 6 caracteres"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cerrarDialogCrear}>
              Cancelar
            </Button>
            <Button onClick={crearUsuario}>
              Crear Usuario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmación de eliminación */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="text-red-500" />
              ¿Eliminar usuario?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminarán todos los datos asociados al usuario
              (productos, configuraciones y proyecciones).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminarUsuario} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default Administracion;

